import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { GetWalletAPI, WalletAPI } from '@orionprotocol/wallet-api';
import { i18n } from '@lingui/core';
import {
  privateKeyStorage,
  sentryLogger,
  wallets,
  WalletsNames,
} from '@orionprotocol/common';
import { useToast } from '@orionprotocol/ui';

 
import { getAvailableNetworks, getWalletType } from '../redux/selectors';
import { appSlice, userSlice } from '../redux/slices';

export type { WalletAPI };

const walletAPIsGetters: Record<string, GetWalletAPI> = wallets.reduce(
  (acc, { name, getAPI }) =>
    getAPI
      ? {
          ...acc,
          [name]: getAPI,
        }
      : acc,
  {},
);

let currentWalletAPI: WalletAPI | undefined;

export function useWalletAPI(): WalletAPI | undefined {
  const dispatch = useDispatch();
  const walletType = useSelector(getWalletType);
  const networks = useSelector(getAvailableNetworks);
  const { showToast } = useToast();

  useEffect(() => {
    if (walletType === undefined) {
      const privateKey = privateKeyStorage.getPrivateKey();
      if (privateKey !== undefined) {
        dispatch(
          userSlice.actions.setWallet({
            walletType: WalletsNames.Software,
          }),
        );
      }

      currentWalletAPI = undefined;
    }
  }, [dispatch, walletType]);

  if (walletType !== undefined && currentWalletAPI?.type !== walletType) {
    const getWalletAPI = walletAPIsGetters[walletType];

    if (getWalletAPI !== undefined) {
      try {
        currentWalletAPI = getWalletAPI({
          type: walletType,
          networks,
          onAccountChanged: ({ address, chainId }) => {
            dispatch(
              userSlice.actions.setWallet({
                address,
                chainId,
              }),
            );
          },
          onChainChanged: ({ chainId }) => {
            dispatch(userSlice.actions.setWallet({ chainId }));
            dispatch(appSlice.actions.setAppNetwork(chainId.toString()));
          },
          onDisconnected: () => {
            dispatch(userSlice.actions.disconnectWallet());
          },
        });
      } catch (e) {
        dispatch(userSlice.actions.disconnectWallet());
        sentryLogger.error(e);
        showToast({
          // iconType: 'error',
          text: i18n._(
            'The wallet API failed to load. Please reload the page and try again.',
          ),
        });
      }
    } else {
      dispatch(userSlice.actions.disconnectWallet());
      console.error(`Unknown walletType '${walletType}'`);
      showToast({
        // iconType: 'error',
        text: i18n._(
          'Unknown wallet type. Please reload the page and try again.',
        ),
      });
    }
  }

  return currentWalletAPI;
}
